import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Generate random username from email
const generateUsername = (email) => {
  const adjectives = ['Tasty', 'Fresh', 'Spicy', 'Sweet', 'Savory', 'Crispy', 'Juicy', 'Zesty', 'Creamy', 'Smoky'];
  const nouns = ['Chef', 'Cook', 'Baker', 'Foodie', 'Gourmet', 'Epicure', 'Connoisseur', 'Gastronome', 'Culinary', 'Kitchen'];
  
  const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
  const randomNumber = Math.floor(Math.random() * 1000);
  
  return `${randomAdjective}${randomNoun}${randomNumber}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing user in localStorage
    const savedUser = localStorage.getItem('nutrilens_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // Simulate email verification and login
      setIsLoading(true);
      
      // In a real app, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const userData = {
        id: uuidv4(),
        email,
        username: generateUsername(email),
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        joinedDate: new Date().toISOString(),
        preferences: {
          dietaryRestrictions: [],
          favoriteIngredients: [],
          cuisinePreferences: []
        },
        stats: {
          recipesLiked: 0,
          recipesShared: 0,
          commentsPosted: 0
        }
      };
      
      setUser(userData);
      localStorage.setItem('nutrilens_user', JSON.stringify(userData));
      setIsLoading(false);
      
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  const signup = async (email, password) => {
    try {
      setIsLoading(true);
      
      // Simulate email verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const userData = {
        id: uuidv4(),
        email,
        username: generateUsername(email),
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${email}`,
        joinedDate: new Date().toISOString(),
        preferences: {
          dietaryRestrictions: [],
          favoriteIngredients: [],
          cuisinePreferences: []
        },
        stats: {
          recipesLiked: 0,
          recipesShared: 0,
          commentsPosted: 0
        }
      };
      
      setUser(userData);
      localStorage.setItem('nutrilens_user', JSON.stringify(userData));
      setIsLoading(false);
      
      return { success: true };
    } catch (error) {
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('nutrilens_user');
  };

  const updateUserPreferences = (preferences) => {
    const updatedUser = { ...user, preferences: { ...user.preferences, ...preferences } };
    setUser(updatedUser);
    localStorage.setItem('nutrilens_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    isLoading,
    login,
    signup,
    logout,
    updateUserPreferences,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
